import { ComboboxItem } from '@mantine/core'
import dayjs, { ConfigType } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import humanizeDuration from 'humanize-duration'
import { Translate } from 'react-admin'

const DEFAULT_FORMAT = 'DD-MM-YYYY'
export const LONG_FORMAT = 'DD-MM-YYYY HH:mm:ss'

dayjs.extend(localizedFormat)
dayjs.extend(duration)
dayjs.extend(timezone)
dayjs.extend(utc)

const humanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
})

export const DateHelper = {
  format: (date: ConfigType, format: string = DEFAULT_FORMAT): string => {
    return dayjs(date).format(format)
  },
  isValidDate: (date: string): boolean => {
    return dayjs(date).isValid()
  },
  toDayjs: (date: ConfigType): dayjs.Dayjs => {
    return dayjs(date)
  },
  january2021Date: (): dayjs.Dayjs => dayjs('2021-12-01T23:00:00.000Z'),
  january2023Date: (): dayjs.Dayjs => dayjs('2023-01-01T00:00:00.000Z'),
  currentDate: (): dayjs.Dayjs => dayjs(),
  currentMonth: (): number => parseInt(dayjs().format('M')),
  // months are 1-indexed in the selects and the API, dayjs' own are 0-indexed
  monthNumber: (date: dayjs.Dayjs): number => parseInt(date.format('M')),
  fromMonthYear: (month: number, year: number): dayjs.Dayjs =>
    dayjs(`${year}-${month.toString().padStart(2, '0')}-15`),
  currentYear: (): number => parseInt(dayjs().format('YYYY')),
  compare: (d1: dayjs.ConfigType, d2: dayjs.ConfigType, type: 'before' | 'after'): boolean => {
    if (type === 'before') {
      return dayjs(d1).isBefore(d2)
    }

    return dayjs(d1).isAfter(d2)
  },
  getMonths: (t: Translate): ComboboxItem[] => {
    let m = 1
    const months = []

    while (m <= 12) {
      months.push({
        value: m.toString(),
        label: `${m.toString().padStart(2, '0')} - ${t(`_generics.month.${m}`)}`,
      })
      m++
    }

    return months
  },
  getYears: () => {
    let y = dayjs().year()
    const years = []

    while (y >= 1900) {
      years.push(y--)
    }

    return years
  },
  getConvertedTime: (time: number | undefined, unit: 's' | 'm' | 'ms' = 'ms', round = false) => {
    let ms = time ?? 0

    if (unit === 's') {
      ms = ms * 1000
    } else if (unit === 'm') {
      ms = ms * 60 * 1000
    }

    return humanizer(ms, {
      largest: 2,
      units: ['y', 'mo', 'w', 'd', 'h', 'm', 's', 'ms'],
      round,
      spacer: ' ',
    })
  },
  getDigitalTime: (time: number | undefined, unit: 's' | 'm' | 'ms' = 'ms') => {
    return dayjs.duration(time ?? 0, unit).format('HH:mm:ss')
  },
  getQuotaResetTime: () => {
    const tz = dayjs.tz.guess()
    return `${dayjs('09:00', 'HH:mm').tz(tz).format('LT')} (${tz})`
  },
}
